import { isSensorPanelMapAction } from '../sensors/sensor-panel-map-action-types';
import { useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { QueryableEntityType } from 'mark43-federated-search';
import { useRedirectHandler } from './use-redirect-handler';
import { useOpenWorkspacePanel } from '../panels/use-open-workspace-panel';
import { WorkspacePanel } from '../panels/workspace-panel';
import { useFrUnitsListFullscreen } from '../fr-units-list/use-fr-units-list-fullscreen';
import {
    ActionHandler,
    useIntegratedActionBroadcaster,
} from '../action-broadcaster/use-integrated-action-broadcaster';
import { useEsriMapHandleViewLocationOnMapAction } from '../map-window/hooks/use-esri-map-handle-view-location-on-map-action';
import { usePhoneCallsStore } from '../phone-calls/use-phone-calls';
import {
    EVENTS_WINDOW_PATH,
    EVENT_SEARCH_WINDOW_PATH,
    MAP_WINDOW_PATH,
    FR_DEX_WINDOW_PATH,
    FR_EDIT_UNIT_PATH,
    UNIT_STATUS_SEARCH_WINDOW_PATH,
    ROOT_PATH,
    FR_EVENTS_LIST_PATH,
    FR_UNITS_LIST_PATH,
    FR_CREATE_EVENT_SUB_PATH,
    FR_EDIT_EVENT_SUB_PATH,
    FR_EVENT_ROOT_PATH,
} from './constants';
import { useOpenUnitHistoryPanel } from '../unit-history/hooks/use-open-unit-history-panel';
import { useApplicationType } from '../common/hooks/use-application-type';
import { useDexResults } from '../data-exchange/results/use-dex-results';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { useNavDrawers } from '../fr-navigation/components/use-nav-drawer';
import { useTransitionUnitStatusMutation } from '../unit-status/use-transition-unit-status-mutation';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useSelectForm } from '../federated-search/hooks/use-select-form';
import { useEnabledFeatures } from '../common/hooks/enabled-features';
import { navigateToDexForm } from './helpers';

export const RouteManager = observer(function RouteManager() {
    useRedirectHandler();
    const { isWeb } = useApplicationType();
    const openWorkspacePanel = useOpenWorkspacePanel();
    const transitionUnitStatus = useTransitionUnitStatusMutation();
    const navigate = useNavigate();
    const esriMapHandleViewLocationOnMapAction = useEsriMapHandleViewLocationOnMapAction();
    const openUnitHistoryPanel = useOpenUnitHistoryPanel();
    const phoneCallsStore = usePhoneCallsStore();
    const { dexResultsStore } = useDexResults();
    const { isFirstResponder } = useApplicationType();
    const unitAssignment = useUnitAssignment();
    const { toggleDrawer: toggleUnitLocationDrawer } = useNavDrawers('unit-location', {
        unitId: unitAssignment?.unitId,
    });
    const { toggleDrawer: toggleMessagingDrawer } = useNavDrawers('fr-messaging');
    const { toggleDrawer: toggleUnitsDrawer } = useNavDrawers('fr-units-list');
    const { expandToFullscreen, closeFullscreen } = useFrUnitsListFullscreen();
    // Tracks the most recently processed action.id. See the
    // UNITS_LIST_EXPANDED case for why this is necessary.
    const lastProcessedActionIdRef = useRef<string | null>(null);

    const { unitStateDataById } = useBootstrapState();
    const unitStateDatum =
        unitAssignment?.unitStateData &&
        unitStateDataById[
            unitAssignment.unitStateData?.id
        ].firstResponderTransitionToUnitStateIds.map((id) => unitStateDataById[id]);
    const atSceneState = unitStateDatum?.find((usd) => usd.unitStateGroup === 'AT_SCENE');
    const location = useLocation();

    const { selectForm } = useSelectForm();
    const { dexNextEnabled } = useEnabledFeatures();

    const openEventPanelActionHandler: ActionHandler<'EVENT_V2'> = useCallback(
        (action) => {
            switch (action.type) {
                case 'OPEN_SCHEDULED_EVENT_PANEL': {
                    openWorkspacePanel<'SCHEDULED_EVENT'>({
                        shouldFocus: action.payload.shouldFocus,
                        workspacePanelConfig: {
                            workspacePanelType: 'SCHEDULED_EVENT',
                            linkedEntityType: 'SCHEDULED_AGENCY_EVENT',
                            linkedEntityId: action.payload.eventId,
                            ...(action.payload.workspacePanelConfig ?? {}),
                        },
                        matchExistingPanel: (workspacePanel) => {
                            if (!action.payload.eventId) {
                                return false;
                            }
                            return (
                                workspacePanel.linkedEntityType === 'SCHEDULED_AGENCY_EVENT' &&
                                workspacePanel.linkedEntityId === action.payload.eventId
                            );
                        },
                    });
                    return true;
                }
                case 'OPEN_EVENT_PANEL': {
                    openWorkspacePanel<'EVENT'>({
                        shouldFocus: action.payload.shouldFocusPanel,
                        workspacePanelConfig: {
                            workspacePanelType: 'EVENT',
                            linkedEntityType: 'CAD_AGENCY_EVENT',
                            linkedEntityId: action.payload.eventId,
                            panelState: action.payload.workspacePanelConfig?.panelState,
                            ...(action.payload.workspacePanelConfig ?? {}),
                        },
                        matchExistingPanel: (workspacePanel) => {
                            if (!action.payload.eventId) {
                                return false;
                            }
                            return (
                                workspacePanel.linkedEntityType === 'CAD_AGENCY_EVENT' &&
                                workspacePanel.linkedEntityId === action.payload.eventId &&
                                workspacePanel.workspacePanelType === 'EVENT'
                            );
                        },
                    });
                    return true;
                }
                case 'OPEN_BOLO_PANEL': {
                    openWorkspacePanel<'BOLO'>({
                        shouldFocus: action.payload.shouldFocus,
                        workspacePanelConfig: {
                            workspacePanelType: 'BOLO',
                            linkedEntityType: 'BOLO',
                            linkedEntityId: action.payload.boloId,
                            panelState: action.payload.workspacePanelConfig?.panelState,
                            ...(action.payload.workspacePanelConfig ?? {}),
                        },
                        matchExistingPanel: (workspacePanel) => {
                            if (!action.payload.boloId) {
                                return false;
                            }
                            return (
                                workspacePanel.linkedEntityType === 'BOLO' &&
                                workspacePanel.linkedEntityId === action.payload.boloId &&
                                workspacePanel.workspacePanelType === 'BOLO'
                            );
                        },
                    });
                    return true;
                }
                case 'OPEN_ATTACHMENTS_PANEL': {
                    const linkedEntityType =
                        action.payload.entityType === 'BOLO' ? 'BOLO' : 'CAD_AGENCY_EVENT';
                    const linkedEntityId = action.payload.entityId;
                    openWorkspacePanel<'MEDIA_ATTACHMENTS'>({
                        shouldFocus: action.payload.shouldFocus,
                        workspacePanelConfig: {
                            workspacePanelType: 'MEDIA_ATTACHMENTS',
                            linkedEntityType,
                            linkedEntityId,
                            panelState: {
                                ...action.payload.workspacePanelConfig?.panelState,
                                attachmentId: action.payload.attachmentId,
                                sortOption: action.payload.sortOption,
                                isReadOnly: action.payload.isReadOnly,
                                entityNumber: action.payload.entityNumber,
                            },
                            ...(action.payload.workspacePanelConfig ?? {}),
                        },
                        matchExistingPanel: (workspacePanel) => {
                            if (!linkedEntityId) {
                                return false;
                            }
                            const isMatch =
                                workspacePanel.linkedEntityType === linkedEntityType &&
                                workspacePanel.linkedEntityId === linkedEntityId &&
                                workspacePanel.workspacePanelType === 'MEDIA_ATTACHMENTS';
                            if (isMatch) {
                                const mediaPanel = workspacePanel as WorkspacePanel<'MEDIA_ATTACHMENTS'>;
                                mediaPanel.updatePanelState(
                                    'attachmentId',
                                    action.payload.attachmentId
                                );
                                mediaPanel.updatePanelState(
                                    'sortOption',
                                    action.payload.sortOption
                                );
                                mediaPanel.updatePanelState(
                                    'entityNumber',
                                    action.payload.entityNumber
                                );
                            }
                            return isMatch;
                        },
                    });
                    return true;
                }
                case 'OPEN_UNIT_HISTORY_PANEL_IN_EVENT_WINDOW': {
                    const { label, value } = action.payload;

                    openUnitHistoryPanel({
                        label,
                        value,
                    });
                    return true;
                }
            }
        },
        [openUnitHistoryPanel, openWorkspacePanel]
    );
    useIntegratedActionBroadcaster({
        id: 'EVENT_PANEL_OPENER',
        windowType: 'EVENT_V2',
        handler: openEventPanelActionHandler,
    });

    const mapActionHandler = useCallback(
        (action) => {
            // Sensor panel actions share the MAP_V2 channel and are handled by
            // `SensorPanelMapActions`; claiming them here would ack them twice.
            if (isSensorPanelMapAction(action)) {
                return false;
            }
            esriMapHandleViewLocationOnMapAction(action);
            return true;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            esriMapHandleViewLocationOnMapAction,
            phoneCallsStore.amlCallsList,
            phoneCallsStore.activePhoneCallsList,
        ]
    );
    useIntegratedActionBroadcaster({
        id: 'MAP_ACTIONS',
        windowType: 'MAP_V2',
        // electron viewLocationOnMap worked as expected without ignoreActiveWorkspaceWindowType. but web will get 'has not been handled' warning
        ignoreActiveWorkspaceWindowType: isWeb,
        handler: mapActionHandler as ActionHandler<'MAP_V2'>,
    });

    const rootActionHandler: ActionHandler<'ROOT'> = useCallback(
        (action) => {
            if (isWeb) {
                switch (action.type) {
                    case 'OPEN_EVENT_V2_WINDOW': {
                        navigate(EVENTS_WINDOW_PATH);
                        return true;
                    }
                    case 'OPEN_MAP_V2_WINDOW': {
                        navigate(MAP_WINDOW_PATH);
                        return true;
                    }
                    case 'OPEN_EVENT_SEARCH_V2_WINDOW': {
                        navigate(EVENT_SEARCH_WINDOW_PATH);
                        return true;
                    }
                }
            }
        },
        [navigate, isWeb]
    );
    useIntegratedActionBroadcaster({
        id: 'ROUTE_MANAGER',
        windowType: 'ROOT',
        ignoreActiveWorkspaceWindowType: true,
        handler: rootActionHandler,
    });

    const frRootActionHandler = useCallback(
        (action) => {
            if (!isFirstResponder) return;

            switch (action.type) {
                case 'DATA_EXCHANGE_OPEN_FORM_VEHICLE_BY_PLATE': {
                    dexResultsStore.setFrDexPageViewMode('NEW_FORM');

                    navigateToDexForm({
                        dexNextEnabled,
                        navigate,
                        selectForm,
                        entityType: QueryableEntityType.VEHICLE,
                        field: 'LicensePlateNumber',
                        fallbackTab: 'VEHICLE_MASTER_QUERY',
                    });

                    return true;
                }
                case 'DATA_EXCHANGE_OPEN_FORM_PERSON_BY_LICENSE': {
                    dexResultsStore.setFrDexPageViewMode('NEW_FORM');

                    navigateToDexForm({
                        dexNextEnabled,
                        navigate,
                        selectForm,
                        entityType: QueryableEntityType.PERSON,
                        field: 'OperatorLicenseNumber',
                        fallbackTab: 'PERSON_MASTER_QUERY',
                        fallbackFocusField: 'By License',
                    });

                    return true;
                }
                case 'DATA_EXCHANGE_OPEN_FORM_PERSON_BY_NAME': {
                    dexResultsStore.setFrDexPageViewMode('NEW_FORM');

                    navigateToDexForm({
                        dexNextEnabled,
                        navigate,
                        selectForm,
                        entityType: QueryableEntityType.PERSON,
                        field: 'NameFirst',
                        fallbackTab: 'PERSON_MASTER_QUERY',
                        fallbackFocusField: 'By Name',
                    });

                    return true;
                }
                case 'DATA_EXCHANGE_OPEN_RESULTS': {
                    dexResultsStore.setFrDexPageViewMode('RESULTS');
                    navigate(FR_DEX_WINDOW_PATH);
                    return true;
                }
                case 'EDIT_UNIT': {
                    navigate(FR_EDIT_UNIT_PATH);
                    return true;
                }
                case 'CAD_SEARCH': {
                    navigate(EVENT_SEARCH_WINDOW_PATH);
                    return true;
                }
                case 'UNIT_STATUS_SEARCH': {
                    navigate(UNIT_STATUS_SEARCH_WINDOW_PATH);
                    return true;
                }
                case 'UNASSIGNED_EVENTS': {
                    navigate(FR_EVENTS_LIST_PATH, { state: { tab: 'UNASSIGNED' } });
                    return true;
                }
                case 'ASSIGNED_EVENTS': {
                    navigate(FR_EVENTS_LIST_PATH, { state: { tab: 'ASSIGNED' } });
                    return true;
                }
                case 'ALL_EVENTS': {
                    navigate(FR_EVENTS_LIST_PATH, { state: { tab: 'ALL' } });
                    return true;
                }
                case 'CURRENT_ASSIGNMENT': {
                    navigate(ROOT_PATH);
                    return true;
                }
                case 'UPDATE_UNIT_LOCATION': {
                    toggleUnitLocationDrawer();
                    return true;
                }
                case 'TOGGLE_MESSAGING_PANEL': {
                    toggleMessagingDrawer();
                    return true;
                }
                case 'TOGGLE_PINNED_MESSAGES_VIEW': {
                    return;
                }
                case 'UNITS': {
                    toggleUnitsDrawer();
                    return true;
                }
                case 'UNITS_LIST_EXPANDED': {
                    // The action broadcaster re-delivers the same action when
                    // this handler's dep array changes mid-flight, thus a single keypress runs both
                    // expandToFullscreen *and* closeFullscreen, producing a
                    // visible flicker.

                    // As the duplicate is always the one immediately preceding, we store single ID
                    // and skip processing if matched.

                    // Returning false on the skip path avoids the broadcaster's ack-lookup
                    // for an already-acked action, which would otherwise fire
                    // a NO_ACTIVE_ACTION_FOUND log.
                    if (lastProcessedActionIdRef.current === action.id) {
                        return false;
                    }
                    lastProcessedActionIdRef.current = action.id;
                    if (location.pathname === FR_UNITS_LIST_PATH) {
                        closeFullscreen();
                    } else {
                        expandToFullscreen();
                    }
                    return true;
                }
                case 'CHANGE_UNIT_STATE': {
                    const nextUnitState = unitStateDatum?.find(
                        (i) => i.id === action.linkedEntityId
                    );

                    if (nextUnitState) {
                        if (
                            nextUnitState.isEventRelated &&
                            unitAssignment?.assignedEvent?.eventId
                        ) {
                            transitionUnitStatus.mutateAsync({
                                eventId: unitAssignment.assignedEvent.eventId,
                                unitStateData: nextUnitState,
                            });
                        } else {
                            transitionUnitStatus.mutateAsync({
                                unitStateData: nextUnitState,
                            });
                        }
                    }
                    return true;
                }
                case 'SELF_INITIATE': {
                    if (action.linkedEntityId) {
                        navigate(
                            `${FR_EVENT_ROOT_PATH}/${FR_CREATE_EVENT_SUB_PATH}?selfInitiateCallForServiceId=${action.linkedEntityId}`
                        );
                    }

                    return true;
                }
                case 'EVENT_SAVE': {
                    if (
                        location.pathname.includes(FR_EVENT_ROOT_PATH) &&
                        (location.pathname.includes(FR_EDIT_EVENT_SUB_PATH) ||
                            location.pathname.includes(FR_CREATE_EVENT_SUB_PATH))
                    ) {
                        navigate(location.pathname, { state: { shouldBeSaved: true } });
                    }
                    return true;
                }
                case 'AT_SCENE': {
                    if (atSceneState) {
                        transitionUnitStatus.mutateAsync({ unitStateData: atSceneState });
                    }
                    return true;
                }
                case 'OPEN_FR_MAP_V2_WINDOW': {
                    navigate(MAP_WINDOW_PATH);
                    return true;
                }
            }
            esriMapHandleViewLocationOnMapAction(action);
            return true;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            navigate,
            isFirstResponder,
            dexResultsStore,
            atSceneState,
            esriMapHandleViewLocationOnMapAction,
            location,
            unitAssignment,
            dexNextEnabled,
            expandToFullscreen,
            closeFullscreen,
        ]
    );

    useIntegratedActionBroadcaster({
        id: 'FR_ROUTE_MANAGER',
        windowType: 'ROOT',
        ignoreActiveWorkspaceWindowType: true,
        handler: frRootActionHandler as ActionHandler<'ROOT'>,
    });

    return null;
});
